import _, { cloneDeep, isNumber } from 'lodash'
import {
    AXIS_DEFAULT_VALUES,
    AXIS_TEXT_DEFAULT_VALUES,
    BAR_DEFAULT_VALUES,
    GAUGE_DEFAULT_VALUES,
    LEGEND_DEFAULT_VALUES,
    PIE_DEFAULT_VALUES,
    RADIAL_DEFAULT_VALUES,
    TITLE_DEFAULT_VALUES,
    LINE_DEFAULT_VALUES,
    LABEL_DEFAULT_VALUES,
} from '../../components/screens/Explorer/DrawerTabs/StyleTab/styleConsts'
import { getDefaultChartPreferences } from './chartPreferences'
import { getIsDarkTheme } from '../../utils/themeHandler'

/**
 * The ECharts option for a Scoop chart: ChartState's base option plus theme, style
 * preferences, overrides, font scaling and chart-type specifics. Moved verbatim from the
 * Insight component (getOptionWithOverrides and its helpers) so the Scoop app and the
 * Domain Intelligence Portal build charts with the same code. Inputs are the Insight props
 * it read: chartProperties (a ChartState with data processed), config, theme,
 * workspaceMetadata, chat, slack, embeddedSizeProps.
 */
export function buildInsightOption({
    chartProperties,
    config,
    theme,
    workspaceMetadata,
    chat,
    slack,
    embeddedSizeProps,
}) {
    const applyFontScale = (overrides, width, height) => {
        overrides.title.textStyle.fontSize =
            (((overrides.title.textStyle.fontSize ?? TITLE_DEFAULT_VALUES.textStyle.fontSize) /
                overrides.dimensions.width) *
                width +
                ((overrides.title.textStyle.fontSize || TITLE_DEFAULT_VALUES.textStyle.fontSize) /
                    overrides.dimensions.height) *
                    height) /
            2

        overrides.legend.textStyle.fontSize =
            (((overrides.legend.textStyle.fontSize || LEGEND_DEFAULT_VALUES.textStyle.fontSize) /
                overrides.dimensions.width) *
                width +
                ((overrides.legend.textStyle.fontSize || LEGEND_DEFAULT_VALUES.textStyle.fontSize) /
                    overrides.dimensions.height) *
                    height) /
            2

        overrides.legend.itemSize =
            (((overrides.legend.itemSize || LEGEND_DEFAULT_VALUES.itemSize) /
                overrides.dimensions.width) *
                width +
                ((overrides.legend.itemSize || LEGEND_DEFAULT_VALUES.itemSize) /
                    overrides.dimensions.height) *
                    height) /
            2

        if (config.seriesType === 'pie' || config.seriesType === 'donut') {
            overrides.label.fontSize =
                (((overrides.label.fontSize || LABEL_DEFAULT_VALUES.fontSize) /
                    overrides.dimensions.width) *
                    width +
                    ((overrides.label.fontSize || LABEL_DEFAULT_VALUES.fontSize) /
                        overrides.dimensions.height) *
                        height) /
                2
        }

        overrides.radialBar.radiusAxis.axisLabel.fontSize =
            (((overrides.radialBar.radiusAxis.axisLabel.fontSize ||
                RADIAL_DEFAULT_VALUES.radiusAxis.axisLabel.fontSize) /
                overrides.dimensions.width) *
                width +
                ((overrides.radialBar.radiusAxis.axisLabel.fontSize ||
                    RADIAL_DEFAULT_VALUES.radiusAxis.axisLabel.fontSize) /
                    overrides.dimensions.height) *
                    height) /
            2

        overrides.radialBar.label.fontSize =
            (((overrides.radialBar.label.fontSize || RADIAL_DEFAULT_VALUES.label.fontSize) /
                overrides.dimensions.width) *
                width +
                ((overrides.radialBar.label.fontSize || RADIAL_DEFAULT_VALUES.label.fontSize) /
                    overrides.dimensions.height) *
                    height) /
            2

        overrides.gauge.detail.fontSize =
            (((overrides.gauge.detail.fontSize || GAUGE_DEFAULT_VALUES.detail.fontSize) /
                overrides.dimensions.width) *
                width +
                ((overrides.gauge.detail.fontSize || GAUGE_DEFAULT_VALUES.detail.fontSize) /
                    overrides.dimensions.height) *
                    height) /
            2

        overrides.gauge.title.fontSize =
            (((overrides.gauge.title.fontSize || GAUGE_DEFAULT_VALUES.title.fontSize) /
                overrides.dimensions.width) *
                width +
                ((overrides.gauge.title.fontSize || GAUGE_DEFAULT_VALUES.title.fontSize) /
                    overrides.dimensions.height) *
                    height) /
            2

        overrides.gauge.axisLabel.fontSize =
            (((overrides.gauge.axisLabel.fontSize || GAUGE_DEFAULT_VALUES.axisLabel.fontSize) /
                overrides.dimensions.width) *
                width +
                ((overrides.gauge.axisLabel.fontSize || GAUGE_DEFAULT_VALUES.axisLabel.fontSize) /
                    overrides.dimensions.height) *
                    height) /
            2

        overrides.xAxis.forEach((axis) => {
            axis.axisLabel.fontSize =
                (((axis.axisLabel.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                    overrides.dimensions.width) *
                    width +
                    ((axis.axisLabel.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                        overrides.dimensions.height) *
                        height) /
                2

            axis.nameTextStyle.fontSize =
                (((axis.nameTextStyle.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                    overrides.dimensions.width) *
                    width +
                    ((axis.nameTextStyle.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                        overrides.dimensions.height) *
                        height) /
                2
        })

        overrides.yAxis.forEach((axis) => {
            axis.axisLabel.fontSize =
                (((axis.axisLabel.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                    overrides.dimensions.width) *
                    width +
                    ((axis.axisLabel.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                        overrides.dimensions.height) *
                        height) /
                2

            axis.nameTextStyle.fontSize =
                (((axis.nameTextStyle.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                    overrides.dimensions.width) *
                    width +
                    ((axis.nameTextStyle.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize) /
                        overrides.dimensions.height) *
                        height) /
                2
        })
    }

    const applyLegendIconSizeTransformation = (option) => {
        const size = option.legend.itemSize || 10
        option.legend.itemWidth = size
        option.legend.itemHeight = size
        delete option.legend.itemSize
    }

    const ensureAxisOverrides = (optionAxis, overridesAxis, referenceAxis) => {
        if (optionAxis.length > overridesAxis.length) {
            optionAxis.forEach((_, i) => {
                overridesAxis[i] = overridesAxis[i] || { ...referenceAxis[0] }
            })
        }
    }

    const dataContainsNegatives = (option) => {
        return option.series.some((s) => {
            return s.data.some((d) => {
                if (typeof d === 'number') {
                    return d < 0
                } else if (d && Array.isArray(d) && d.length > 1) {
                    return typeof d[1] === 'number' ? d[1] < 0 : d[0] < 0
                } else if (typeof d === 'object' && d !== null) {
                    return d.value < 0
                }
                return false
            })
        })
    }

    const applyColumnResize = (option, overrides) => {
        if (
            (config.seriesType === 'bar' || config.seriesType === 'column') &&
            chartProperties.categoryLegendData?.length > 1
        ) {
            let divisor = 1
            if (chartProperties.categoryAxisValues > 1 && !config.stacked)
                divisor = chartProperties.categoryAxisValues?.length - 1
            if (chartProperties.categoryLegendData && !config.stacked)
                divisor = divisor + chartProperties.categoryLegendData?.length - 1
            if (config.selectedItems.length > 1 && config.stacked) {
                divisor = config.selectedItems.length
            }
            const barGapPercentage = parseFloat(overrides.bar?.barGap?.split('%')[0] || '10')
            option.series.forEach((s, i) => {
                const availableSpace = 100 - (divisor - 1) * (barGapPercentage / divisor)
                let barWidth =
                    (overrides.bar?.barWidth?.split('%')[0] || BAR_DEFAULT_VALUES.barWidth) /
                    divisor
                barWidth = (barWidth * availableSpace) / 100
                if (barWidth) {
                    option.series[i] = {
                        ...s,
                        ...overrides.bar,
                        barWidth: `${barWidth}%`,
                    }
                }
            })
        } else if (config.seriesTypeMap.size > 0) {
            const columnNames = Array.from(config.seriesTypeMap.entries())
                .filter(
                    ([columnName, columnType]) => columnType === 'bar' || columnType === 'column'
                )
                .map(([columnName]) => columnName)
            if (columnNames.length > 0) {
                const divisor = columnNames.length
                const barGapPercentage = parseFloat(overrides.bar?.barGap?.split('%')[0] || '10')
                option.series.forEach((s, i) => {
                    const availableSpace = 100 - (divisor - 1) * barGapPercentage
                    let barWidth =
                        (overrides.bar?.barWidth?.split('%')[0] || BAR_DEFAULT_VALUES.barWidth) /
                        divisor
                    barWidth = (barWidth * availableSpace) / 100
                    if (barWidth && columnNames.includes(s.name)) {
                        option.series[i] = {
                            ...s,
                            ...overrides.bar,
                            barWidth: `${barWidth}%`,
                        }
                    }
                })
            }
        }
    }

    const adjustBorderRadius = (radius) => {
        const tempRadius = [...radius]
        return [tempRadius[2], tempRadius[0], tempRadius[1], tempRadius[3]]
    }

    const buildMap = (length, series) => {
        let lastValidIndicesMap = {}
        for (let i = 0; i < length; i++) {
            series.forEach((series, index) => {
                const value = series.data[i]
                const isValid = Array.isArray(value)
                    ? value[1] !== 0 && value[1] !== null
                    : value !== 0 && value !== null
                if (isValid) lastValidIndicesMap[i] = index
            })
        }
        return lastValidIndicesMap
    }

    const applyStringBreak = (str) => {
        if (!str || typeof str !== 'string') return str
        const middle = Math.floor(str.length / 2)
        let leftPos = str.lastIndexOf(' ', middle)
        let rightPos = str.indexOf(' ', middle)
        if (leftPos === -1) leftPos = 0
        if (rightPos === -1) rightPos = str.length
        const breakPoint = middle - leftPos <= rightPos - middle ? leftPos : rightPos
        if (breakPoint > 0 && breakPoint < str.length) {
            return str.slice(0, breakPoint) + '\n' + str.slice(breakPoint + 1)
        } else {
            return str.slice(0, middle) + '\n' + str.slice(middle)
        }
    }

    const getOptionWithOverrides = () => {
        const defaultTheme = JSON.parse(getDefaultChartPreferences(chat && getIsDarkTheme()))
        let option = _.cloneDeep(chartProperties.getOption())
        if (theme)
            option = _.cloneDeep(
                chartProperties.getOption(theme.themeID, workspaceMetadata, slack ? theme : null)
            )
        let overrides = _.cloneDeep(config.styleOverrides)
        let baseTheme = JSON.parse(getDefaultChartPreferences(chat && getIsDarkTheme()))
        // Make sure that overrides axis length are up to date and match the option axis length
        if (config.seriesType === 'bar') {
            ensureAxisOverrides(option.yAxis, overrides.xAxis, overrides.xAxis)
            ensureAxisOverrides(option.xAxis, overrides.yAxis, overrides.yAxis)
        } else {
            ensureAxisOverrides(option.yAxis, overrides.yAxis, overrides.yAxis)
            ensureAxisOverrides(option.xAxis, overrides.xAxis, overrides.xAxis)
        }

        if (theme && theme.chartPreferences) {
            let preferences =
                typeof theme.chartPreferences === 'string'
                    ? JSON.parse(theme.chartPreferences)
                    : theme.chartPreferences
            //Convert to arrays for backwards compatibility
            if (!Array.isArray(preferences.yAxis))
                preferences.yAxis = cloneDeep([preferences.yAxis])
            if (!Array.isArray(preferences.xAxis))
                preferences.xAxis = cloneDeep([preferences.xAxis])
            // Apply preferences style to each of the axes.
            overrides.yAxis.forEach((axis, i) => {
                if (preferences.yAxis[i])
                    overrides.yAxis[i] = _.merge(
                        _.cloneDeep(preferences.yAxis[i]),
                        _.cloneDeep(axis)
                    )
                else
                    overrides.yAxis[i] = _.merge(
                        _.cloneDeep(preferences.yAxis[0]),
                        _.cloneDeep(axis)
                    )
            })
            overrides.xAxis.forEach((axis, i) => {
                if (preferences.xAxis[i])
                    overrides.xAxis[i] = _.merge(
                        _.cloneDeep(preferences.xAxis[i]),
                        _.cloneDeep(axis)
                    )
                else
                    overrides.xAxis[i] = _.merge(
                        _.cloneDeep(preferences.xAxis[0]),
                        _.cloneDeep(axis)
                    )
            })
            preferences = _.omit(preferences, 'xAxis')
            preferences = _.omit(preferences, 'yAxis')
            overrides = _.merge(_.cloneDeep(preferences), _.cloneDeep(overrides))
        } else {
            // Apply default theme to each of the axes.
            overrides.yAxis.forEach(
                (axis, i) =>
                    (overrides.yAxis[i] = _.merge(_.cloneDeep(baseTheme.yAxis), _.cloneDeep(axis)))
            )
            overrides.xAxis.forEach(
                (axis, i) =>
                    (overrides.xAxis[i] = _.merge(_.cloneDeep(baseTheme.xAxis), _.cloneDeep(axis)))
            )
            baseTheme = _.omit(baseTheme, 'xAxis')
            baseTheme = _.omit(baseTheme, 'yAxis')
            overrides = _.merge(_.cloneDeep(baseTheme), _.cloneDeep(overrides))
        }
        if (config.seriesType === 'bar') {
            let radius = overrides.bar?.itemStyle?.borderRadius || [5, 5, 0, 0]
            if (overrides.bar?.itemStyle?.borderRadius) {
                overrides.bar.itemStyle.borderRadius = adjustBorderRadius(radius)
            }
        }

        // remove position overrides if present (backwards comp)
        overrides.xAxis.forEach((a) => {
            if (a.position) delete a.position
        })
        overrides.yAxis.forEach((a) => {
            if (a.position) delete a.position
        })

        if (
            config.seriesType === 'pie' ||
            config.seriesType === 'donut' ||
            config.seriesType === 'gauge' ||
            config.seriesType === 'radialBar'
        ) {
            overrides.xAxis.forEach((axis) => (axis.show = false))
            overrides.yAxis.forEach((axis) => (axis.show = false))
            overrides.legend.icon = 'none'
            option.tooltip.trigger = 'item'
            if (overrides.grid?.tooltip?.axisPointer) {
                overrides.grid.tooltip.trigger = 'item'
            }
        } else if (
            config.stacked &&
            (config.seriesType === 'bar' || config.seriesType === 'column')
        ) {
            option.tooltip.trigger = 'item'
            if (overrides.grid?.tooltip?.axisPointer) {
                overrides.grid.tooltip.trigger = 'item'
            }
        }

        if (config.seriesType === 'heatmap') {
            option.tooltip.trigger = 'item'
            if (overrides.grid?.tooltip?.axisPointer) {
                overrides.grid.tooltip.trigger = 'item'
            }
        }

        overrides.yAxis.forEach((axis) => {
            if (axis.show === undefined) axis.show = AXIS_DEFAULT_VALUES.show
            if (axis.splitLine.show === undefined)
                axis.splitLine.show = AXIS_DEFAULT_VALUES.splitLine.show
            if (axis.splitLine.lineStyle.color === undefined)
                axis.splitLine.lineStyle.color = AXIS_DEFAULT_VALUES.splitLine.lineStyle.color
            if (axis.splitLine.lineStyle.type === undefined)
                axis.splitLine.lineStyle.type = AXIS_DEFAULT_VALUES.splitLine.lineStyle.type
            if (axis.splitLine.lineStyle.width === undefined)
                axis.splitLine.lineStyle.width = AXIS_DEFAULT_VALUES.splitLine.lineStyle.width
        })

        // apply pie/donut configs (this has to be done before the font scale)
        if (config.seriesType === 'donut') {
            overrides.donut = _.merge(
                JSON.parse(getDefaultChartPreferences()).donut,
                overrides.donut
            )
            option.series.forEach((s, i) => {
                option.series[i] = { ...s, ...overrides.donut }
            })
        } else {
            overrides.pie = _.merge(JSON.parse(getDefaultChartPreferences()).pie, overrides.pie)
            if (option.series.some((s) => s.type === 'pie')) {
                option.series.forEach((s, i) => {
                    option.series[i] = { ...s, ...overrides.pie }
                })
            }
        }

        // apply font scale
        if (embeddedSizeProps) {
            const height = embeddedSizeProps.containerSize.height
            const width = embeddedSizeProps.containerSize.width
            applyFontScale(overrides, width, height)
        }

        if (config.seriesType === 'bar') {
            const xAxisOverrides = overrides.yAxis || []
            const yAxisOverrides = overrides.xAxis || []

            option.xAxis.forEach((axisObject, i) => {
                if (xAxisOverrides[i]) {
                    option.xAxis[i] = {
                        ...axisObject,
                        ...xAxisOverrides[i],
                        axisLabel: {
                            ...axisObject.axisLabel,
                            ...xAxisOverrides[i]?.axisLabel,
                            align: 'bottom',
                        },
                    }
                }
            })

            option.yAxis.forEach((axisObject, i) => {
                if (yAxisOverrides[i]) {
                    option.yAxis[i] = {
                        ...axisObject,
                        ...yAxisOverrides[i],
                        position: 'left',
                        axisLabel: {
                            ...axisObject.axisLabel,
                            ...yAxisOverrides[i]?.axisLabel,
                            align: 'right',
                        },
                    }
                }
            })

            if (option.series.some((s) => s.yAxisIndex !== undefined)) {
                option.series.forEach((s) => {
                    s.xAxisIndex = s.yAxisIndex
                    delete s.yAxisIndex
                })
            }
        } else {
            // Distributing the overrides to xAxis and yAxis for every other chart
            option.yAxis.forEach((axisObject, i) => {
                option.yAxis[i] = _.merge(axisObject, overrides.yAxis[i])
                if (['top', 'bottom'].includes(option.yAxis[i].position)) {
                    option.yAxis[i].position = ['left', 'right'][i] || 'right'
                    option.yAxis[i].axisLabel.align = ['left', 'right'].filter(
                        (o) => o !== option.yAxis[i].position
                    )[0]
                }
            })
            option.xAxis.forEach((axisObject, i) => {
                delete overrides.xAxis[i].name
                option.xAxis[i] = _.merge(axisObject, overrides.xAxis[i])
            })
            if (option.series.some((s) => s.xAxisIndex !== undefined)) {
                option.series.forEach((s, i) => {
                    s.yAxisIndex = s.xAxisIndex
                    delete s.xAxisIndex
                })
            }
        }

        // apply bar/waterfall configs
        if (config.seriesType === 'waterfall') {
            option.series.forEach((s, i) => {
                if (s.name === 'positive') {
                    option.series[i].itemStyle = {
                        color: overrides.waterfall?.upColor || defaultTheme.waterfall.upColor,
                    }
                    option.series[i].data[0] = {
                        ...option.series[i].data[0],
                        itemStyle: {
                            color:
                                overrides.waterfall.startColor || defaultTheme.waterfall.startColor,
                        },
                    }
                }
                if (s.name === 'negative') {
                    option.series[i].itemStyle = {
                        color: overrides.waterfall.downColor || defaultTheme.waterfall.downColor,
                    }
                    const lastIndex = option.series[i].data.length - 1
                    option.series[i].data[lastIndex] = {
                        ...option.series[i].data[lastIndex],
                        itemStyle: {
                            color: overrides.waterfall.endColor || defaultTheme.waterfall.endColor,
                        },
                    }
                }
            })
        } else {
            if (option.series.some((s) => s.type === 'bar')) {
                option.series.forEach((s, i) => {
                    option.series[i] = { ...s, ...overrides.bar }
                })
            }
        }

        // apply line configs
        if (option.series.some((s) => s.type === 'line')) {
            option.series.forEach((s, i) => {
                option.series[i] = { ...s, ...overrides.line }
            })
        }

        if (config.seriesType === 'radialBar') {
            overrides.radialBar = _.merge(
                JSON.parse(getDefaultChartPreferences()).radialBar,
                overrides.radialBar
            )
            option = {
                ...option,
                angleAxis: {
                    ...overrides.radialBar.angleAxis,
                    data: { ...chartProperties?.categoryAxisValues },
                },
                polar: overrides.radialBar.polar,
                radiusAxis: overrides.radialBar.radiusAxis,
            }
            const showAsStacked = overrides.radialBar.stack
            option.series.forEach((s, i) => {
                if (showAsStacked) option.series[i].stack = 'total'
                option.series[i].coordinateSystem = 'polar'
                option.series[i].type = 'bar'
                option.series[i].label = {
                    position: 'middle',
                    formatter: '{b}: {c}',
                    ...overrides.radialBar.label,
                }
                option.series[i] = {
                    ...s,
                    emphasis: overrides.radialBar.emphasis,
                    itemStyle: overrides.radialBar.itemStyle,
                    barWidth: overrides.radialBar.barWidth,
                }
            })
        }

        // apply pictorial configs
        if (config.seriesType === 'pictorialBar') {
            const xAxisCopy = _.cloneDeep(option.xAxis)
            const yAxisCopy = _.cloneDeep(option.yAxis)

            if (overrides.pictorialBar.showAsBar) {
                option.xAxis = yAxisCopy
                option.yAxis = xAxisCopy

                option.series.forEach((seriesItem) => {
                    if (seriesItem.yAxisIndex !== undefined) {
                        seriesItem.xAxisIndex = seriesItem.yAxisIndex
                        delete seriesItem.yAxisIndex
                    }
                })
            } else {
                option.xAxis = xAxisCopy
                option.yAxis = yAxisCopy

                option.series.forEach((seriesItem) => {
                    if (seriesItem.xAxisIndex !== undefined) {
                        seriesItem.yAxisIndex = seriesItem.xAxisIndex
                        delete seriesItem.xAxisIndex
                    }
                })
            }
            option.series.forEach((series, seriesIndex) => {
                if (!overrides.pictorialBar.data[seriesIndex]) {
                    overrides.pictorialBar.data[seriesIndex] = []
                }

                series.barGap = overrides.pictorialBar.barGap
                series.barCategoryGap = overrides.pictorialBar.barCategoryGap

                series.data.forEach((d, i) => {
                    if (typeof d === 'number') {
                        series.data[i] = Object.assign(
                            { value: d },
                            overrides.pictorialBar.data[seriesIndex][i]
                        )
                    } else {
                        series.data[i] = Object.assign(
                            {},
                            d,
                            overrides.pictorialBar.data[seriesIndex][i]
                        )
                    }
                })
            })
        }

        // apply pie/donut configs
        if (
            (config.seriesType === 'donut' || config.seriesType === 'pie') &&
            config.categoryAxis === 'Time'
        ) {
            option.series.forEach((series, i) => {
                series.data.forEach((d, j) => {
                    option.series[i].data[j] = {
                        name: d.value[0],
                        value: d.value[1],
                    }
                })
            })
        }

        if (config.seriesType === 'gauge') {
            overrides.gauge = _.merge(
                JSON.parse(getDefaultChartPreferences()).gauge,
                overrides.gauge
            )
            overrides.tooltip.show = false
            overrides.legend.show = false
            option.series.forEach((s, i) => {
                s.type = 'gauge'
                if (
                    overrides.gauge?.data &&
                    overrides.gauge.data[0]?.value &&
                    isNumber(overrides.gauge.data[0].value)
                ) {
                    option.series[i].data = overrides.gauge?.data
                } else {
                    const name = chartProperties?.categoryAxisValues?.[0] || ''
                    let value = (
                        ((chartProperties?.series[0]?.data[0]?.value ||
                            chartProperties?.series[0]?.data[0]) *
                            100) /
                        chartProperties?.series[0]?.data.reduce((a, b) => a + (b?.value || b), 0)
                    ).toFixed(3)
                    option.series[i].data = [{ value, name }]
                }
                option.series[i] = {
                    ...s,
                    ...overrides.gauge,
                    data: option.series[i].data,
                }
            })
        }

        applyColumnResize(option, overrides)

        //Remove radius for bar or column charts if they are stacked
        if (
            config.stacked &&
            (config.seriesType === 'bar' || config.seriesType === 'column') &&
            config.drillAttribute
        ) {
            const stacks = {}

            option.series.forEach((series, i) => {
                const stackName = series.stack || `stack_${i}`
                if (!stacks[stackName]) stacks[stackName] = []

                const hasValidData = Array.isArray(series.data)
                    ? series.data.some((d) => d !== 0 && d !== null)
                    : series.data !== 0 && series.data !== null

                if (hasValidData) stacks[stackName].push(series)
            })

            Object.values(stacks).forEach((stackSeries) => {
                const radiusSeries = []
                for (let i = 0; i < stackSeries[0]?.data.length; i++) {
                    let lastValidIndex = -1
                    stackSeries.forEach((series, j) => {
                        const value = Array.isArray(series.data[i])
                            ? series.data[i][1]
                            : series.data[i]
                        if (value !== 0 && value !== null) lastValidIndex = j
                    })
                    if (lastValidIndex !== -1) {
                        radiusSeries.push({
                            data: i,
                            series: lastValidIndex,
                        })
                    }
                }

                stackSeries.forEach((series, index) => {
                    series.data = series.data.map((d, i) => {
                        const isTop = radiusSeries.some((r) => r.data === i && r.series === index)
                        const value = typeof d === 'object' ? d?.value || d : d
                        const itemStyle = isTop
                            ? {
                                  borderRadius: overrides.bar?.itemStyle?.borderRadius || [
                                      5, 5, 0, 0,
                                  ],
                              }
                            : { borderRadius: [0, 0, 0, 0] }

                        return {
                            value,
                            itemStyle,
                        }
                    })
                })
            })
        }

        // apply max legends and max chars
        const max = overrides.legend?.maxLegends || LEGEND_DEFAULT_VALUES.maxLegends
        const maxChars =
            overrides.legend?.textStyle.maxCharacters ||
            LEGEND_DEFAULT_VALUES.textStyle.maxCharacters
        const maxAxisLabelChars =
            overrides.xAxis[0]?.axisLabel?.maxCharacters || AXIS_TEXT_DEFAULT_VALUES.maxCharacters
        if (option.legend?.data?.length > 0) {
            option.legend.formatter = (s) =>
                s.length > maxChars ? s.substring(0, maxChars) + '...' : s
            if (max !== 'all') option.legend.data = option?.legend?.data?.slice(0, max)
        }
        if (config.seriesType !== 'scatter') {
            const targetAxis = config.seriesType === 'bar' ? 'yAxis' : 'xAxis'
            option[targetAxis] = option[targetAxis].map((axis) => ({
                ...axis,
                axisLabel: {
                    ...axis.axisLabel,
                    formatter: (s) =>
                        s.length > maxAxisLabelChars
                            ? s.substring(0, maxAxisLabelChars) + '...'
                            : s,
                },
            }))
        }

        // omit props we dont wanna merge
        ;[
            'waterfall',
            'bar',
            'line',
            'pie',
            'donut',
            'pictorialBar',
            'gauge',
            'radialBar',
            'table',
            'xAxis',
            'yAxis',
        ].forEach((key) => {
            overrides = _.omit(overrides, [key])
        })
        option = _.merge(option, overrides)

        applyLegendIconSizeTransformation(option)
        if (dataContainsNegatives(option) && config.seriesType !== 'scatter') {
            const axis = config.seriesType === 'bar' ? 'xAxis' : 'yAxis'
            option[axis].forEach((axis) => {
                axis.min = function (value) {
                    const absMax = Math.max(Math.abs(value.min), Math.abs(value.max))
                    return -Math.ceil(absMax)
                }
                axis.max = function (value) {
                    const absMax = Math.max(Math.abs(value.min), Math.abs(value.max))
                    return Math.ceil(absMax)
                }
            })
            if (
                config.stacked &&
                (config.seriesType === 'bar' || config.seriesType === 'column') &&
                config.drillAttribute
            ) {
                option.series.forEach((s, i) => {
                    const defaultRadius = s.itemStyle?.borderRadius || [5, 5, 0, 0]
                    s.data = s.data.map((d, j) => {
                        let value
                        if (Array.isArray(d)) {
                            value = d[1]
                        } else if (typeof d === 'object' && d !== null) {
                            value = d.value
                        } else {
                            value = d
                        }
                        const adjustedRadius = [...defaultRadius]
                        if (value < 0 && d?.itemStyle?.borderRadius !== [0, 0, 0, 0]) {
                            const tempRadius = [...defaultRadius]
                            if (config.seriesType === 'bar') {
                                adjustedRadius[0] = tempRadius[1]
                                adjustedRadius[3] = tempRadius[2]
                                adjustedRadius[1] = tempRadius[0]
                                adjustedRadius[2] = tempRadius[3]
                            } else {
                                adjustedRadius[0] = tempRadius[2]
                                adjustedRadius[1] = tempRadius[3]
                                adjustedRadius[2] = tempRadius[0]
                                adjustedRadius[3] = tempRadius[1]
                            }
                            d.itemStyle.borderRadius = adjustedRadius
                            const fixRoundedTopIndex = i - 1
                            const fixTopData = option.series[fixRoundedTopIndex].data[j]
                            let fixedValue
                            if (Array.isArray(fixTopData)) {
                                fixedValue = fixTopData[1]
                            } else if (typeof fixTopData === 'object' && fixTopData !== null) {
                                fixedValue = fixTopData.value
                            } else {
                                fixedValue = fixTopData
                            }
                            const adjustedFixedRadius = [...defaultRadius]
                            if (config.seriesType === 'bar') {
                                adjustedFixedRadius[0] = defaultRadius[3]
                                adjustedFixedRadius[2] = defaultRadius[1]
                            }
                            option.series[fixRoundedTopIndex].data[j] = {
                                value: fixedValue,
                                itemStyle: {
                                    borderRadius: adjustedFixedRadius,
                                },
                            }
                        }
                        return {
                            value,
                            itemStyle: {
                                borderRadius: d?.itemStyle?.borderRadius || [5, 5, 0, 0],
                            },
                        }
                    })
                })
            } else {
                option.series.forEach((s) => {
                    const defaultRadius = s.itemStyle?.borderRadius || [5, 5, 0, 0]
                    s.data = s.data.map((d) => {
                        let value
                        if (Array.isArray(d)) {
                            value = typeof d[1] === 'number' ? d[1] : d[0]
                        } else if (typeof d === 'object' && d !== null) {
                            value = d.value
                        } else {
                            value = d
                        }

                        const adjustedRadius = [...defaultRadius]
                        if (value < 0) {
                            const tempRadius = [...defaultRadius]
                            if (config.seriesType === 'bar') {
                                adjustedRadius[0] = tempRadius[1]
                                adjustedRadius[3] = tempRadius[2]
                                adjustedRadius[1] = tempRadius[0]
                                adjustedRadius[2] = tempRadius[3]
                            } else {
                                adjustedRadius[0] = tempRadius[2]
                                adjustedRadius[1] = tempRadius[3]
                                adjustedRadius[2] = tempRadius[0]
                                adjustedRadius[3] = tempRadius[1]
                            }
                        }

                        return {
                            value,
                            itemStyle: {
                                ...d?.itemStyle,
                                borderRadius: adjustedRadius,
                            },
                        }
                    })
                })
            }
        }

        // apply legend position
        const legendPosition = overrides.legend?.scoopPosition || 'bottom'
        switch (legendPosition) {
            case 'top':
                option.grid.top = '20%'
                option.grid.bottom = '5%'
                option.grid.left = '10%'
                option.grid.right = '10%'
                option.legend.left = 'center'
                option.legend.top = '10%'
                option.legend.orient = 'horizontal'
                break
            case 'bottom':
                option.grid.top = '15%'
                option.grid.bottom = '15%'
                option.grid.left = '10%'
                option.grid.right = '10%'
                option.legend.left = 'center'
                option.legend.top = 'bottom'
                option.legend.orient = 'horizontal'
                break
            case 'left':
                option.grid.top = '10%'
                option.grid.bottom = '5%'
                option.grid.left = '20%'
                option.grid.right = '10%'
                option.legend.left = 'left'
                option.legend.top = 'middle'
                option.legend.orient = 'vertical'
                break
            case 'right':
                option.grid.top = '10%'
                option.grid.bottom = '5%'
                option.grid.left = '10%'
                option.grid.right = '20%'
                option.legend.left = 'right'
                option.legend.top = 'middle'
                option.legend.orient = 'vertical'
                break
        }

        // fix sorting for bar charts
        if (
            config.sortColumns.some((sortingOption) =>
                ['asc', 'desc'].includes(sortingOption.order)
            ) &&
            config.seriesType === 'bar'
        ) {
            option.series.forEach((series) => {
                if (series.data) series.data = series.data.toReversed()
            })
            option.yAxis.forEach((axis) => {
                if (axis.data) axis.data = axis.data.toReversed()
            })
        }

        // check for axis title removal
        if (config.selectedItems.length > 4) {
            if (config.seriesType === 'bar') {
                option.xAxis.forEach((axis) => {
                    axis.name = ''
                })
            }
            if (['column', 'line', 'area'].includes(config.seriesType)) {
                option.yAxis.forEach((axis) => {
                    axis.name = ''
                })
            }
        } else {
            if (config.seriesType === 'bar') {
                option.xAxis.forEach((axis) => {
                    if (slack || chat) {
                        axis.nameLocation = 'middle'
                        axis.nameGap = 40
                    } else {
                        axis.name = axis.name.split(' ').join('\n')
                    }
                })
            }
            if (config.seriesType === 'scatter') {
                option.yAxis.forEach((axis) => {
                    axis.nameLocation = 'middle'
                    axis.nameGap = 120
                    axis.axisLabel.margin = 30
                })
                option.xAxis.forEach((axis) => {
                    axis.nameLocation = 'middle'
                    axis.nameGap = 80
                    axis.axisLabel.margin = 30
                })
            }
            if (['column', 'line', 'area'].includes(config.seriesType)) {
                option.yAxis.forEach((axis) => {
                    if (axis.name.includes('\n') && slack) {
                        axis.nameLocation = 'middle'
                        axis.nameGap = 120
                    }
                })
                if (option.title?.text?.split(' ').length > 3 && !slack) {
                    option.title.text = applyStringBreak(option.title.text)
                    option.grid.top = '20%'
                }
            }
        }

        // reduce legend left padding
        option.legend.textStyle.padding = [0, 0, 0, -3]

        // Apply label config to all series
        for (const s of option.series) {
            if (config.seriesType === 'column' || config.seriesType === 'bar') {
                s.label = {
                    position: config.styleOverrides?.bar?.position || BAR_DEFAULT_VALUES.position,
                }
            } else if (config.seriesType === 'line') {
                s.label = {
                    position: config.styleOverrides?.line?.position || LINE_DEFAULT_VALUES.position,
                }
            } else if (config.seriesType === 'pie' || config.seriesType === 'donut') {
                s.label = {
                    position: config.styleOverrides?.pie?.position || PIE_DEFAULT_VALUES.position,
                    show: slack
                        ? true
                        : (config.styleOverrides?.label?.show ?? LABEL_DEFAULT_VALUES.show),
                }
            }
        }
        if (slack && (config.seriesType === 'pie' || config.seriesType === 'donut'))
            option.label.show = true

        if (config.seriesType === 'heatmap') {
            // Ensure we have the required data structures for heatmap customization
            if (!option.xAxis?.[0]?.data || !option.yAxis?.[0]?.data) {
                // Skip heatmap-specific customizations if data is missing
                return option
            }

            option.grid.left = '5%'
            option.grid.top = '10%'
            option.grid.right = '5%'
            option.grid.bottom = '15%' // Add more space for labels
            delete option.label.color

            // Adjust x-axis labels based on data length
            const xDataLength = option.xAxis[0].data.length
            option.xAxis[0].axisLabel.rotate = xDataLength > 5 ? 45 : 0
            option.xAxis[0].axisLabel.fontSize = xDataLength > 10 ? 10 : xDataLength > 5 ? 12 : 14
            option.xAxis[0].axisLabel.interval = xDataLength > 20 ? 'auto' : 0 // Show all labels unless too many

            // Adjust y-axis labels based on data length
            const yDataLength = option.yAxis[0].data.length
            option.yAxis[0].axisLabel.fontSize = yDataLength > 20 ? 10 : yDataLength > 10 ? 12 : 14
            option.yAxis[0].axisLabel.interval = yDataLength > 30 ? 'auto' : 0 // Show all labels unless too many

            // Adjust cell labels - hide if too many categories in either axis
            if (yDataLength > 15 || xDataLength > 40) {
                option.series[0].label.show = false // Hide labels when too many rows or columns
            } else {
                option.series[0].label.fontSize = xDataLength > 10 ? 6 : xDataLength > 5 ? 8 : 12
                option.series[0].label.rotate = xDataLength > 5 ? 45 : 0
            }

            // Add dataZoom for large datasets (mouse wheel zoom only, no visible slider)
            if (yDataLength > 20) {
                option.dataZoom = [
                    {
                        type: 'inside', // Only mouse wheel/trackpad zoom, no visible slider
                        yAxisIndex: 0,
                        start: 0,
                        end: 100, // Start with full view showing all categories
                    },
                ]
            }
        }

        if (option.title.top === '0%' && slack) option.title.top = '5%'
        if (option.legend.top === 'bottom' && slack) option.legend.top = '95%'
        if (chat) {
            if (getIsDarkTheme()) option.backgroundColor = '#1A1A1A'

            if (config.seriesType === 'bar') {
                option.grid.left = '3%'
                option.xAxis.forEach((axis) => {
                    axis.name = axis.name.split('\n').join(' ')
                })
            }

            if (config.seriesType !== 'bar') {
                option.yAxis.forEach((axis) => {
                    if (axis?.name?.includes('\n')) {
                        const sentences = axis?.name?.split('\n')
                        sentences.forEach((sentence, index) => {
                            const words = sentence.trim().split(' ')
                            if (words.length > 4) {
                                // Add ellipsis after the 4th word if there are more than 4 words
                                sentences[index] = words.slice(0, 4).join(' ') + '...'
                            }
                        })
                        axis.name = sentences.join('\n')
                    } else {
                        const length = axis?.name?.split(' ').length
                        if (length >= 4) {
                            const a = applyStringBreak(axis.name)
                            axis.name = a
                        }
                    }

                    if (config.seriesType === 'scatter') axis.nameGap = 100
                })
            }

            // Enable labels for pie/donut charts in chat mode
            if (config.seriesType === 'pie' || config.seriesType === 'donut') {
                if (option.series && Array.isArray(option.series)) {
                    option.series = option.series.map((series) => {
                        // Calculate total for percentage calculation
                        const total = series.data.reduce((sum, item) => {
                            const value = typeof item === 'object' ? item.value : item
                            return sum + (value || 0)
                        }, 0)

                        // Transform data to hide labels and lines for slices < 5%
                        const transformedData = series.data.map((item) => {
                            const value = typeof item === 'object' ? item.value : item
                            const percentage = total > 0 ? (value / total) * 100 : 0

                            if (percentage < 5) {
                                // Hide label and labelLine for small slices
                                return {
                                    ...(typeof item === 'object' ? item : { value: item }),
                                    label: { show: false },
                                    labelLine: { show: false },
                                }
                            }
                            return item
                        })

                        return {
                            ...series,
                            data: transformedData,
                            label: {
                                ...series.label,
                                show: true,
                                position: 'outside',
                                fontSize: 12,
                            },
                            labelLine: {
                                show: true,
                            },
                            emphasis: {
                                disabled: true, // Disable all emphasis effects
                            },
                        }
                    })
                }
            }
        }

        applyAutoLayout(option)
        return option
    }

    /*
     * Layout rules the engine decides for every chart (added 2026-09-24):
     * - A chart with a single series needs no legend: the title already names it.
     * - Category labels rotate when they do not fit their slot. Rotation used to be decided by
     *   category count in ChartState.getOption, but the default style preferences (rotate: 0)
     *   were merged over it, so it never took effect. A rotation set in the style tab (non-zero)
     *   is still respected; 0 is treated as "automatic". Every category label is shown.
     */
    const applyAutoLayout = (option) => {
        const series = Array.isArray(option.series) ? option.series : []
        const drawn = series.filter((s) => s && s.type !== 'pie' && s.type !== 'gauge')
        if (drawn.length === 1 && option.legend) option.legend.show = false

        const width = embeddedSizeProps?.containerSize?.width
        const axes = config.seriesType === 'bar' ? [] : [].concat(option.xAxis || [])
        axes.forEach((axis) => {
            if (!axis || axis.type !== 'category' || !Array.isArray(axis.data) || !width) return
            if (config.categoryAxis === 'Time') return
            const label = axis.axisLabel || (axis.axisLabel = {})
            if (label.rotate) return
            const fontSize = label.fontSize || AXIS_TEXT_DEFAULT_VALUES.fontSize || 12
            const maxChars = label.maxCharacters || AXIS_TEXT_DEFAULT_VALUES.maxCharacters || 20
            const longest = Math.max(
                0,
                ...axis.data.map((v) => Math.min(String(v ?? '').length, maxChars))
            )
            // The plot is narrower than the chart (axis labels and padding take ~25%).
            const slot = (width * 0.75) / Math.max(axis.data.length, 1)
            const needed = longest * fontSize * 0.62
            if (needed > slot) label.rotate = needed > slot * 2.5 ? 90 : 35
            // Every label is shown, flat or rotated: left to itself ECharts drops labels it
            // measures as crowded, e.g. the middle one of three categories.
            label.interval = 0
        })
    }

    return getOptionWithOverrides()
}
